import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import useScopedMetrics from "../../hooks/useScopedMetrics";
import { resolveCardWindow } from "../../helpers/cardWindow";
import { kindOfKpi } from "../../helpers/kpiTargetScale";
import { UseCompanyWorkspaces } from "../../hooks/company";
import { getCompanyHolidays } from "../../redux/company/actions";
import { holidayDaySet, periodDaysFor, periodEndFor, workingDaysBetween } from "../../helpers/workingDays";
import { resolveWindow } from "../../pages/CompanyMetrics/dashboards/useWorkspaceData";
import useScheduledSplit from "./useScheduledSplit";
import { lookAheadDaysFor, lookAheadEnd } from "./lookAhead";
import { companyToday } from "../../helpers/companyTime";
import useTotalRevenue, { findTotalRevenueKpi, findScheduledRevenueKpi, isRollupPick } from "./useTotalRevenue";
import useDerivedRevenueMetrics, { isDerivedMetricId } from "../../hooks/useDerivedRevenueMetrics";
import { derivedAmountOf, derivedSeriesOf } from "../../helpers/derivedGoal";
import { AVG_DAILY_REVENUE_ID } from "../../helpers/derivedMetricIds";
import amountThrough from "./amountThrough";

/* formatFull / formatCompact moved to ./format.js and periodDaysFor to
   helpers/workingDays.js — both so a consumer can have them WITHOUT this
   module, whose chain reaches an indexedDB call at load time (hooks/kpi.js).
   Import them from there; nothing re-exports them here, deliberately. */

const cellValue = (cell) => {
  if (cell == null) return 0;
  if (typeof cell === "number") return cell;
  if (typeof cell.amt === "number") return cell.amt;
  return 0;
};

/**
 * One raw point per day across [from, to] — same candidate-key recipe
 * CustomWidget's MetricBlock uses for its own line/area/bar graphs (a card
 * can carry the catalog title, the live _id, or a scoped card's remapped
 * _seriesIds, and seriesByDay may be keyed by any of those — see
 * useScopedMetrics's own title-aliasing, added for this exact mismatch). Not
 * cumulative — the chart sums a running total itself, so this stays reusable
 * for a future "daily" view. */
export const dailySeriesFor = (seriesByDay, keys, from, to) => {
  if (!seriesByDay || !from || !to || !from.isValid?.() || !to.isValid?.()) return [];
  const candidates = [...new Set((keys || []).filter(Boolean).map(String))];
  if (!candidates.length) return [];
  const cursor = from.clone().startOf("day");
  const end = to.clone().startOf("day");
  const out = [];
  let guard = 0;
  while (cursor.isSameOrBefore(end, "day") && guard < 400) {
    const day = cursor.format("YYYY-MM-DD");
    const bucket = seriesByDay[day] || {};
    let value = 0;
    for (let i = 0; i < candidates.length; i += 1) {
      const cell = bucket[candidates[i]];
      if (cell != null) { value = cellValue(cell); break; }
    }
    out.push({ date: day, value });
    cursor.add(1, "day");
    guard += 1;
  }
  return out;
};

/**
 * Goal Progress reads the SAME single-metric shape "Build your own" cards use
 * (`s.metrics: [{ metricId, graph, color }]`) rather than inventing a new
 * `metricId` field. That shape (and `goalTarget`) is already threaded through
 * settingsFor and layoutStore's sanitizer (see widgetRegistry.jsx `metrics`),
 * so a bare unlisted field would silently vanish on reopen — the exact trap
 * the Payroll vs Revenue `rows` field hit before it was named there.
 */
export const metricIdOf = (s) => {
  const id = Array.isArray(s?.metrics) && s.metrics[0] ? s.metrics[0].metricId : null;
  return id ? String(id) : null;
};

const nameKey = (k) => (k?.title || k?.name || "").trim().toLowerCase();
const looseKey = (str) => (str || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

/**
 * Find the picked metric's live definition — scoped pool first (has `amount`
 * for the resolved window), falling back to the raw company catalog (a
 * metric outside the "live" cap, or before the scope finishes loading).
 *
 * The Analytics metric picker (`metricCatalog`, built in DashboardBuilder from
 * useDashboardMetrics) dedupes same-named KPIs across workspaces and uses the
 * METRIC'S TITLE as its `id` (`id: title` in useDashboardMetrics.js) — it is
 * NOT a Mongo `_id`. Matching only by `_id` here (as the scoped-metrics pool
 * and companyKpis both key on) meant a picked metric could never resolve: the
 * pick saved fine, but the card stayed stuck on "Pick a metric" forever. Name
 * match is the normal path, same fallback ScopedCustomWidget's findDefAnywhere
 * uses for the identical reason. */
export const resolveGoalMetric = (metricId, pool, companyKpis) => {
  if (!metricId) return null;
  const id = String(metricId);
  const byId = (pool || []).find((k) => k && String(k._id) === id);
  if (byId) return byId;

  const want = id.trim().toLowerCase();
  const wantLoose = looseKey(id);
  const byName = (pool || []).find((k) => {
    const nk = nameKey(k);
    return nk === want || looseKey(nk) === wantLoose;
  });
  if (byName) return byName;

  if (companyKpis && typeof companyKpis === "object") {
    const all = Object.values(companyKpis).flat().filter(Boolean);
    const def = all.find((k) => k && (
      String(k._id) === id || nameKey(k) === want || looseKey(nameKey(k)) === wantLoose
    ));
    if (def) return { ...def, amount: Number(def.amount) || 0 };
  }
  return null;
};

/**
 * @param {object} settings   the widget's saved options (s)
 * @param {string} workspaceId  ctx.workspaceId — used only when no scope is picked
 * @param {string} boardWindow  ctx.boardWindow || ctx.periodName
 * @param {object} boardCustom  ctx.boardCustom
 */
export default function useGoalProgressData(settings, workspaceId, boardWindow, boardCustom) {
  const companyKpis = useSelector((state) => state.kpiReducer.companyKpis);
  const companyId = useSelector((state) => state.authReducer.userDetails?.companyId);
  /* The company calendar's own holidays — the same list the Calendar page
     shows and the Yeti's pace targets read. Nothing else on a dashboard asks
     for them (they are only fetched when somebody opens the Calendar page),
     so a board opened straight from login had an empty list and this card
     would quietly divide a monthly goal by 23 working days instead of 22.
     Asked for once per company; the saga caches it into redux for every other
     card that reads the same list. Same fetch ProfitTrend does for its own
     pace line. */
  const holidays = useSelector((state) => state.companyReducer.holidays);
  const dispatch = useDispatch();
  useEffect(() => {
    if (companyId && !(holidays || []).length) dispatch(getCompanyHolidays());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  const workspaces = UseCompanyWorkspaces(true) || [];
  const s = settings || {};
  const workspaceIds = (s.workspaceIds || []).filter(Boolean).map(String);
  let reading = [];
  if (workspaceIds.length) reading = workspaceIds;
  else if (workspaceId) reading = [String(workspaceId)];
  const { window: win, custom } = resolveCardWindow(s, { window: boardWindow || null, custom: boardCustom || null });

  /* Team total by default, not "average per player" (the app-wide default
     for a scoped card — see ScopeWindow's avgMode). A goal is a period
     TARGET — "$2 of $10" on a real revenue metric was this default dividing
     the team's real total by however many scorers it was spread across.
     Flip it only when the admin explicitly asks for the per-player average
     in Basics. */
  const data = useScopedMetrics(reading, {
    window: win,
    custom,
    show: s.avgMode === "avg" ? "avg" : "total",
  });

  const metricId = metricIdOf(s);
  const pool = [...(data.kpis || []), ...(data.kpisAll || [])];

  /* Derived metrics (Average Daily Revenue, Technicians' Revenue per Hour, Beat
     the Yeti rate) are not KPI documents: resolveGoalMetric can never find one,
     which left a card that had one picked stuck on "Pick a metric". They are
     answered by the same hook Build Your Own reads them from, so the two cards
     can not disagree about the figure. Idle (fetches nothing) unless one is
     the pick. */
  const derivedId = isDerivedMetricId(metricId) ? metricId : null;
  const derivedMap = useDerivedRevenueMetrics(derivedId ? [derivedId] : [], win === "year" ? "ytd" : win, custom, reading);
  const derived = derivedId ? derivedMap[derivedId] || null : null;

  const metric = derived
    ? { _id: derivedId, title: derived.title, kind: derived.kind, _derived: true }
    : resolveGoalMetric(metricId, pool, companyKpis);

  /* Total Revenue / Total Scheduled Revenue: read through the IDENTICAL
     pipeline Payroll vs Revenue / Build Your Own use (fetchWorkspaceScores +
     sumScores against the one KPI flagged with the matching rollup
     model.type), so the cards can never disagree — see useTotalRevenue.js.
     Both are company-wide rollups useScopedMetrics deliberately never
     carries (see ScopedCustomWidget's own ROLLUP_KPI_TYPES comment), so
     without this a pick just resolves `metric` from the raw company catalog
     via resolveGoalMetric's fallback — a definition with no live `amount`,
     which is why it read as a permanent 0. Hooks still run when they're not
     the pick (cheaply — no fetch fires without a resolved kpi + real
     workspace/kpi ids), so both stay plain, unconditional hook calls. */
  const revKpi = findTotalRevenueKpi(companyKpis, workspaces);
  const schedKpi = findScheduledRevenueKpi(companyKpis, workspaces);
  const isRevenuePick = isRollupPick(metricId, revKpi, metric, "total revenue", "total_revenue");
  const isScheduledPick = !isRevenuePick
    && isRollupPick(metricId, schedKpi, metric, "total scheduled revenue", "scheduled_revenue");
  const revenue = useTotalRevenue(isRevenuePick ? revKpi : null, win, custom);
  /* Through the END of the period: what is booked for the days still to come
     is the point of this metric (see useTotalRevenue). */
  const aheadDays = lookAheadDaysFor(s, isScheduledPick);
  const scheduled = useTotalRevenue(isScheduledPick ? schedKpi : null, win, custom, true, aheadDays);
  /* Only Total Scheduled Revenue shades its bars by what has been completed;
     idle (fetches nothing) for every other pick. */
  const scheduledSplit = useScheduledSplit(isScheduledPick, win === "year" ? "ytd" : win, custom, aheadDays);
  /* The same Total Revenue day-by-day the Average Daily Revenue card draws: a
     job is filed under the day its work was completed. Total Scheduled Revenue's
     bars are filed under the day a job was BOOKED for, so a job booked on the
     14th and finished on the 19th shades the 14th's bar. This is the figure
     the tooltip sets beside it, so the two cards can be read against each other.
     Idle (fetches nothing) for every other pick. */
  const earnedByDay = useTotalRevenue(isScheduledPick ? revKpi : null, win, custom, true, aheadDays);
  const isRollup = isRevenuePick || isScheduledPick;
  const rollup = isRevenuePick ? revenue : scheduled;

  const kind = isRollup ? "$" : kindOfKpi(metric, null);
  let amount = 0;
  if (isRollup) amount = rollup.amount;
  else if (derived) amount = derivedAmountOf(derived.amount, derived.kind);
  else if (metric) amount = Number(metric.amount) || 0;
  const label = metric ? (metric.title || metric.name || "Metric") : null;
  let hasMetric = !!metric;
  if (isRevenuePick) hasMetric = !!revKpi;
  else if (isScheduledPick) hasMetric = !!schedKpi;

  const seriesKeys = [metricId, metric && metric._id, ...((metric && metric._seriesIds) || [])];
  let dailySeries;
  if (derived) dailySeries = derivedSeriesOf(derived);
  else if (isRollup) dailySeries = rollup.dailySeries;
  else dailySeries = dailySeriesFor(data.seriesByDay, seriesKeys, data.from, data.to);

  /* ── The pace the goal sets ────────────────────────────────────────────
     Daily Pace = the goal for this window ÷ the window's TOTAL working days,
     which is known on the 10th of the month as surely as on the 30th.

     The bounds come from resolveWindow, not from `data.from`/`data.to`: those
     are null until a workspace is being read, and the Total Revenue pick does
     not go through useScopedMetrics at all — the pace has to be the same
     figure either way, and both paths resolve the very same window id. The
     divisor is measured to the PERIOD's end (periodEndFor), never to today:
     a "this week" card draws Monday to today but its goal is still for five
     days, and dividing by the two gone would inflate the pace by more than
     half. */
  const goalTarget = Number(s.goalTarget) > 0 ? Number(s.goalTarget) : 0;
  const customKey = custom ? `${custom.from || ""}~${custom.to || ""}` : "";
  const pace = useMemo(() => {
    const { from, to } = resolveWindow(win === "year" ? "ytd" : win, custom);
    const holidaySet = holidayDaySet(holidays);
    const periodEnd = periodEndFor(win, to, custom);
    const workingDays = workingDaysBetween(from, periodEnd, holidaySet);
    const workingDaysElapsed = workingDaysBetween(from, to, holidaySet);
    const dailyPace = goalTarget > 0 && workingDays > 0 ? goalTarget / workingDays : null;
    return {
      workingDays,
      workingDaysElapsed,
      dailyPace,
      /* What the goal expects by the END OF TODAY — the figure "ahead by" /
         "behind by" is measured against. Pro-rated by working days gone, not
         calendar days, so a Monday morning is not judged as a third of the
         week. */
      expectedToDate: dailyPace != null ? dailyPace * workingDaysElapsed : null,
      /* Every day of the period, whether or not there is a goal: the chart
         needs the `working` flags to be able to plot working days only. */
      /* Total Scheduled Revenue has figures for days that have not happened, so
         no day of its period is "future": the chart draws the whole month, not
         just up to today. The pace above still counts working days to today. */
      periodDays: (() => {
        const chartEnd = lookAheadEnd(periodEnd, aheadDays, companyToday());
        const days = periodDaysFor(from, chartEnd, isScheduledPick ? chartEnd : to, holidaySet, dailyPace);
        if (!chartEnd.isAfter(periodEnd, "day")) return days;
        /* Past the period's end there is no goal to pace against: no pace, and
           a flag so the chart draws these days apart from the period's own. */
        const lastOfPeriod = periodEnd.format("YYYY-MM-DD");
        return days.map((d) => (d.date > lastOfPeriod ? { ...d, pace: null, cumPace: null, ahead: true } : d));
      })(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, customKey, goalTarget, holidays, isScheduledPick, aheadDays]);

  /* The amount the pace is held against: through the end of today, the same
     bound `expectedToDate` counts working days to. Only Total Scheduled Revenue
     differs from `amount` — it also holds the days still to come. */
  const elapsedTo = resolveWindow(win === "year" ? "ytd" : win, custom).to;
  const amountToDate = isScheduledPick && elapsedTo && elapsedTo.isValid()
    ? amountThrough(dailySeries, elapsedTo.format("YYYY-MM-DD"))
    : amount;

  /* Average Daily Revenue is already a per-day figure, so its goal IS the
     pace: $20,000 a day for every working day gone is what the period should
     have brought in so far. Held against the revenue actually taken to date
     (the days that fed the average), it gives the same "Behind by" the Total
     Scheduled Revenue card carries. Other derived picks are rates ($ an hour,
     a share of the team) with no such running total, so they get none. */
  const avgDaily = derivedId === AVG_DAILY_REVENUE_ID;
  const revenueToDate = avgDaily ? dailySeries.reduce((sum, d) => sum + (Number(d.value) || 0), 0) : 0;

  return {
    metricId,
    metric,
    label,
    kind,
    amount,
    amountToDate: avgDaily ? revenueToDate : amountToDate,
    avgDaily,
    dailySeries,
    /* Per-day completed share of Total Scheduled Revenue, or null for every
       other metric (and until it has loaded): the chart draws plain bars. */
    completedSeries: isScheduledPick ? scheduledSplit.series : null,
    /* Revenue completed on each day (see earnedByDay). Null until it has loaded,
       so the tooltip simply leaves the line off rather than showing a 0. */
    earnedSeries: isScheduledPick && earnedByDay.active && !earnedByDay.loading ? earnedByDay.dailySeries : null,
    /* Look-ahead (Total Scheduled Revenue): how many days past the period the
       chart draws, and what they add up to. Not part of `amount`. */
    aheadDays,
    aheadAmount: isScheduledPick ? Number(scheduled.aheadAmount) || 0 : 0,
    /* "idle" | "loading" | "ready" | "error" — lets the card reserve the
       legend's row before the answer lands. */
    splitStatus: isScheduledPick ? scheduledSplit.status : "idle",
    /* Everything the pace line, the working-days-only axis and the caption are
       drawn from — see periodDaysFor. `dailyPace` is null whenever there is no
       goal or no working day to divide by, and the chart falls back to a flat
       goal line rather than drawing nothing. */
    dailyPace: pace.dailyPace,
    workingDays: pace.workingDays,
    workingDaysElapsed: pace.workingDaysElapsed,
    expectedToDate: pace.expectedToDate,
    periodDays: pace.periodDays,
    hasMetric,
    hasWorkspace: !!reading.length,
    /* A derived pick keeps loading until its own figure is in — its inputs
       (company revenue, Technicians' hours, the Yeti roster) arrive on their
       own, not through the scoped read above. */
    loading: (() => {
      if (derived) return !!derived.loading;
      if (isRollup) return rollup.loading;
      return !!data.loading && !!reading.length && !!metricId;
    })(),
    /* Derived figures are rates and averages — dollars per hour, dollars per
       day, a share of the team — none of which is earned a day at a time. A
       running total of them, or a goal pace spread over the working days, is
       not a smaller version of the right answer, it is a meaningless one
       (see index.jsx / GoalTrendChart). */
    additive: !derived,
    /* No day-by-day history at all (Beat the Yeti rate is a standing, as-of
       reading) — nothing for a trend chart to draw, so the card shows a shape. */
    seriesless: !!derived && !dailySeries.length && derived.kind === "%",
    /* The goal a real anchor metric that measures the same thing already has
       (helpers/derivedGoal) — used until the admin types one of their own. */
    defaultGoal: derived && derived.goal ? Number(derived.goal.target) || 0 : 0,
    goalSource: derived && derived.goal ? derived.goal.title || null : null,
    /* The window this card (and so the goal) is actually reading — "This
       month" / "Q3" / a custom range. Same resolved value Basics → Workspaces
       → date range already sets; surfaced here so the goal reads as "the
       goal FOR this period" instead of a bare number with no period in
       sight. */
    win,
    custom,
  };
}
